import styled from 'styled-components'
import produce from 'immer'
import { useImmer } from 'use-immer'
import { Tooltip, Button, Table } from 'antd'
import { isEmpty, isEqual, toString as str, remove, set } from 'lodash'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import Modal from 'elements/Modal'
import { t } from 'helpers/i18n'
import { isReadOnly } from 'helpers/formViews'
import Icon from 'elements/Icon'
import TableRowActions from 'elements/TableRowActions'
import Drawer from 'elements/Drawer'
import LineForm from 'containers/LabelTemplates/FormView/LineForm'
import FieldForm from 'containers/LabelTemplates/FormView/FieldForm'
import { Row, Col } from 'elements/Grid'

const Container = styled.div`
  width: 100%;
  overflow: auto;
`

const Wrapper = styled.div`
  display: flex;

  .ant-btn {
    height: 28px;
    padding: 4px 6px;
  }

  .ant-btn-group {
    margin-right: 6px;

    .ant-btn:first-child {
      padding: 4px 6px;
    }
  }
`

const Fields = styled.div`
  width: 100%;
  display: flex;
  outline: ${(props) => (props.isDraggingOver ? '1px dashed #d9d9d9' : 'none')};
  height: 28px;
`

const Field = styled.div`
  user-select: none;
`

function Component(props) {
  const { lines, onChange, parentRecord, onHighlight } = props

  const [state, updateState] = useImmer({ selectedRowKeys: [] })

  function setState(path, value) {
    updateState((draft) => {
      set(draft, path, value)
    })
  }

  const readOnly = isReadOnly({ props })

  function handleDeleteLines() {
    onChange(
      lines
        .filter((each) => !(state?.selectedRowKeys ?? []).includes(each.id))
        .map((each, index) => ({
          ...each,
          number: index + 1,
          name: `Line ${index + 1}`,
        }))
    )

    setState('selectedRowKeys', [])
  }

  function deleteField(lineIndex, fieldIndex) {
    Modal.confirm({
      autoFocusButton: 'ok',
      title: t('confirmDeleteSelectedItems'),
      okType: 'danger',
      okText: t('delete'),
      cancelText: t('cancel'),
      onOk: () => {
        onChange(
          produce(lines, (draft) => {
            draft[lineIndex].fields.splice(fieldIndex, 1)
          })
        )
      },
    })
  }

  function handleLineDrawerSaveAndClose(saved) {
    saved.name = `Line ${saved.number}`

    if (!saved.id) {
      saved.id = Date.now() * -1
    }

    onChange(
      produce(lines, (draft) => {
        remove(draft, (one) => one.id === saved.id)

        draft.splice(saved.number - 1, 0, { ...saved })

        draft.forEach((line, index) => {
          line.number = index + 1
          line.name = `Line ${index + 1}`
        })
      })
    )

    setState('lineDrawerVisible', false)
  }

  function handleFieldDrawerSaveAndClose(saved) {
    if (!saved.id) {
      saved.id = Date.now() * -1
    }

    onChange(
      produce(lines, (draft) => {
        draft.forEach((line) => {
          remove(line.fields, (one) => one.id === saved.id)
        })

        draft.find((one) => one.id === saved.lineId).fields.splice(saved.position - 1, 0, { ...saved })

        draft.forEach((line) => {
          line.fields.forEach((field, index) => {
            field.lineId = line.id
            field.position = index + 1
          })
        })
      })
    )

    setState('fieldDrawerVisible', false)
  }

  function handleDragEnd(result) {
    const { source, destination } = result

    if (isEmpty(destination) || isEqual(source, destination)) {
      return
    }

    onChange(
      produce(lines, (draft) => {
        try {
          const start = draft.find((one) => str(one.id) === source.droppableId)
          const finish = draft.find((one) => str(one.id) === destination.droppableId)

          finish.fields.splice(destination.index, 0, ...start.fields.splice(source.index, 1))
        } catch (error) {
          console.warn(error)
        }

        draft.forEach((line) => {
          line.fields.forEach((field, index) => {
            field.lineId = line.id
            field.position = index + 1
          })
        })
      })
    )
  }

  function createHandleEditFieldClick({ record, field }) {
    return () =>
      updateState((draft) => {
        draft.fieldDrawerTitle = getFieldTypeName(field.fieldType)
        draft.fieldDrawerVisible = true
        draft.fieldDrawerSaving = false
        draft.fieldDrawerLinkTargetRecord = field
        draft.fieldDrawerParentRecord = record
      })
  }

  function createHandleAddFieldClick({ record }) {
    return () =>
      updateState((draft) => {
        draft.fieldDrawerTitle = t('addField')
        draft.fieldDrawerVisible = true
        draft.fieldDrawerSaving = false
        draft.fieldDrawerLinkTargetRecord = null
        draft.fieldDrawerParentRecord = record
      })
  }

  function createHandleEditLineClick({ record }) {
    return () =>
      updateState((draft) => {
        draft.lineDrawerTitle = `${t('line')} ${record.number}`
        draft.lineDrawerVisible = true
        draft.lineDrawerLinkTargetRecord = record
      })
  }

  function getFieldTypeName(fieldType) {
    try {
      return props.labelTemplateFields.find((one) => one.fieldType === fieldType).fieldTypeName
    } catch (error) {
      console.warn(error)
      return t(fieldType)
    }
  }

  return (
    <>
      <Row type="flex" justify="space-between" className="whitespace-nowrap mb-12">
        <Col>
          <TableRowActions
            selectedRowKeys={state.selectedRowKeys}
            allowDelete={!readOnly}
            onDelete={handleDeleteLines}
          />
        </Col>
        <Col className="text-right">
          <Tooltip title={t('addLine')} placement="topRight">
            <Button
              type="primary"
              onClick={() => {
                updateState((draft) => {
                  draft.lineDrawerTitle = t('addLine')
                  draft.lineDrawerVisible = true
                  draft.lineDrawerLinkTargetRecord = null
                })
              }}
              disabled={readOnly}
              size="small"
            >
              <Icon type="Add" bold />
            </Button>
          </Tooltip>
        </Col>
      </Row>
      <Container>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table
            childrenColumnName={[]}
            rowKey={(record) => record.id}
            dataSource={lines}
            size="middle"
            rowSelection={{
              selectedRowKeys: state.selectedRowKeys,
              onChange: (rowKeys) => setState('selectedRowKeys', rowKeys),
            }}
            pagination={false}
            className="mb-24"
          >
            <Table.Column
              key="line"
              title={t('line')}
              dataIndex="name"
              className="whitespace-nowrap"
              render={(text, record, index) => (
                <a onClick={createHandleEditLineClick({ record })}>{`${t('line')} ${record.number}`}</a>
              )}
            />
            <Table.Column
              key="fields"
              title={t('fields')}
              dataIndex="fields"
              className="whitespace-nowrap w-full"
              render={(text, record, lineIndex) => (
                <Wrapper>
                  <Droppable droppableId={str(record.id)} direction="horizontal" index={lineIndex}>
                    {(provided, snapshot) => (
                      <Fields
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        isDraggingOver={snapshot.isDraggingOver}
                      >
                        {(record?.fields ?? []).map((field, fieldIndex) => (
                          <Draggable key={field.id} draggableId={str(field.id)} index={fieldIndex}>
                            {
                              // eslint-disable-next-line no-shadow
                              (provided, snapshot) => (
                                <Field
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="ant-btn-group"
                                  onMouseEnter={() => onHighlight({ lineIndex, fieldIndex })}
                                  onMouseLeave={() => onHighlight({ lineIndex: -1, fieldIndex: -1 })}
                                >
                                  <div
                                    className="ant-btn"
                                    onClick={createHandleEditFieldClick({ record, field })}
                                  >
                                    {getFieldTypeName(field.fieldType)}
                                  </div>
                                  {!readOnly && (
                                    <div
                                      className="ant-btn"
                                      onClick={() => deleteField(lineIndex, fieldIndex)}
                                    >
                                      <Icon type="Clear" />
                                    </div>
                                  )}
                                </Field>
                              )
                            }
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {!readOnly && (
                          <Tooltip title={t('addField')}>
                            <Button onClick={createHandleAddFieldClick({ record })} type="primary">
                              <Icon type="Add" bold />
                            </Button>
                          </Tooltip>
                        )}
                      </Fields>
                    )}
                  </Droppable>
                </Wrapper>
              )}
            />
          </Table>
        </DragDropContext>
      </Container>
      <Drawer
        title={state.lineDrawerTitle}
        size="xs"
        visible={state.lineDrawerVisible}
        onClose={() => setState('lineDrawerVisible', false)}
      >
        <LineForm
          parentRecord={parentRecord}
          siblingRecords={lines}
          linkTargetRecord={state.lineDrawerLinkTargetRecord}
          onSave={() => {}}
          onSaveAndClose={handleLineDrawerSaveAndClose}
          onCancel={() => setState('lineDrawerVisible', false)}
        />
      </Drawer>
      <Drawer
        title={state.fieldDrawerTitle}
        size="xs"
        visible={state.fieldDrawerVisible}
        onClose={() => setState('fieldDrawerVisible', false)}
      >
        <FieldForm
          rootRecord={parentRecord}
          parentRecord={state.fieldDrawerParentRecord}
          linkTargetRecord={state.fieldDrawerLinkTargetRecord}
          onSave={() => {}}
          onSaveAndClose={handleFieldDrawerSaveAndClose}
          onCancel={() => setState('fieldDrawerVisible', false)}
        />
      </Drawer>
    </>
  )
}

export default Component
